import React, { useState, useCallback, useEffect } from "react";
import _ from "underscore";
import styles from "./adhesionConsolidatedData.module.css";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import FileExportIcon from "../../../assets/icons/file-export.svg";
import DatabaseIcon from "../../../assets/icons/users.svg";
import { useInstitution } from "../../../context/InstitutionContext";
import { adhesionConsolidatedData } from "../../../services/education";
import { toast } from "react-toastify";
import loadingGif from "../../../assets/loading.gif";
import api from "../../../http/api";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";

const AdhesionConsolidatedData = () => {
  const { institution } = useInstitution();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [paginationInfo, setPaginationInfo] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchConsolidatedData = useCallback((pageNumber = 1, searchTerm = "") => {
    const debouncedFetch = _.debounce(async () => {
      try {
        setLoading(true);
        
        const response = await adhesionConsolidatedData({
          institutionId: institution.id,
          page: pageNumber,
          pageSize: itemsPerPage,
          searchTerm: searchTerm,
          startDate: null,
          endDate: null
        });
        
        const apiResponse = response.adhesionData;
        
        setLastApiResponse(apiResponse);
        setConsolidatedData(apiResponse.adhesionConsolidatedData);
        setPaginationInfo({
          pageNumber: apiResponse.currentPage,
          pageSize: itemsPerPage,
          totalCount: apiResponse.totalResults,
          totalPages: apiResponse.totalPages,
          hasPreviousPage: apiResponse.currentPage > 1,
          hasNextPage: apiResponse.currentPage < apiResponse.totalPages
        });
      } catch (error) {
        console.error("Erro ao buscar dados consolidados de adesão:", error);
        toast.error("Erro ao buscar dados consolidados de adesão");
        setConsolidatedData([]);
        setPaginationInfo(null);
      } finally {
        setLoading(false);
      }
    }, 1000);
    
    debouncedFetch();
  }, [institution?.id, itemsPerPage]);

  const processedConsolidatedData = consolidatedData ? consolidatedData.map((item) => ({
    studentId: item.studentId || '-',
    dateOfBirth: formatDate(item.dateOfBirth),
    gender: item.gender || '-',
    maritalStatus: item.maritalStatus || '-',
    studentStatus: item.studentStatus || '-',
    courseModality: item.courseModality || '-',
    classCode: item.classCode || '-',
    classStatus: item.classStatus || '-',
    publicationDate: formatDate(item.publicationDate),
    courseName: item.courseName || '-',
    courseArea: item.courseArea || '-',
    courseCity: item.courseCity || '-',
    courseState: item.courseState || '-',
    acceptanceDate: formatDate(item.acceptanceDate),
    preRegistrationDate: formatDate(item.preRegistrationDate),
    registrationDate: formatDate(item.registrationDate),
    enrollmentDate: formatDate(item.enrollmentDate),
    createdAt: formatDateTime(item.createdAt),
    updatedAt: item.updatedAt ? formatDateTime(item.updatedAt) : '-'
  })) : [];

  const handlePageClick = (page) => {
    setCurrentPage(page);
    fetchConsolidatedData(page, searchTerm);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    fetchConsolidatedData(1, term);
  };

  const getVisiblePages = () => {
    if (!paginationInfo) return [];
    
    const totalPages = paginationInfo.totalPages;
    const current = currentPage;
    let visiblePages = [];
    
    if (totalPages <= 7) {
      visiblePages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      if (current <= 4) {
        visiblePages = [1, 2, 3, 4, 5, '...', totalPages];
      } else if (current >= totalPages - 3) {
        visiblePages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        visiblePages = [1, '...', current - 1, current, current + 1, '...', totalPages];
      }
    }
    
    return visiblePages;
  };

  const handleExportCsv = async () => {
    if (!institution?.id) {
      toast.error("Instituição não encontrada");
      return;
    }

    if (!lastApiResponse?.executedQuery) {
      toast.error("Nenhuma consulta disponível para exportação");
      return;
    }

    try {
      setExporting(true);
      toast.info("Preparando exportação...");

      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear - 3}-01-01`;
      const endDate = `${currentYear + 3}-12-31`;

      // Remover paginação da query
      const exportQuery = lastApiResponse.executedQuery
        .replace(/OFFSET @Offset ROWS\s*FETCH NEXT @PageSize ROWS ONLY/gi, '');

      const exportPayload = {
        executedQuery: exportQuery,
        parameters: {
          InstitutionId: institution.id,
          StartDate: startDate,
          EndDate: endDate,
        },
        headers: {
          CorrelationId: "ID de Correlação",
          StudentId: "ID do Aluno",
          DateOfBirth: "Data de Nascimento",
          Gender: "Gênero",
          MaritalStatus: "Estado Civil",
          StudentStatus: "Status do Aluno",
          CourseModality: "Modalidade do Curso",
          ClassCode: "Código da Turma",
          ClassStatus: "Status da Turma",
          PublicationDate: "Data de Publicação",
          CourseName: "Nome do Curso",
          CourseArea: "Área do Curso",
          CourseCity: "Cidade do Curso",
          CourseState: "Estado do Curso",
          AcceptanceDate: "Data de Aceitação",
          PreRegistrationDate: "Data de Pré-Matrícula",
          RegistrationDate: "Data de Matrícula",
          EnrollmentDate: "Data de Inscrição",
          CreatedAt: "Data de Criação",
          UpdatedAt: "Data de Atualização"
        },
        fileName: "dados_adesao_consolidados.csv",
        includeHeaders: true,
        delimiter: ";",
        culture: "pt-BR"
      };

      if (searchTerm) {
        exportPayload.parameters.SearchTerm = searchTerm;
      }

      const response = await api.client.post(
        'api/v1/CsvExport/export-from-query',
        exportPayload,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            institutionId: institution.id
          }
        }
      );

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', exportPayload.fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV exportado com sucesso!");

    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      toast.error("Erro ao exportar dados. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (institution?.id) {
      fetchConsolidatedData(1, "");
    }
  }, [institution?.id, fetchConsolidatedData]);

  return (
    <div className={styles.consolidatedDataContent}>
      {/* Header */}
      <HeaderTitle title="Dados Consolidados de Adesão" icon={DatabaseIcon} />

      <div className={styles.consolidatedDataBody}>
        <div className={styles.tableContainer}>
          {/* Barra de pesquisa e exportação */}
          <div className={styles.tableActions}>
            <div className={styles.searchInputWrapper}>
              <img src={SearchIcon} alt="Pesquisar" className={styles.searchIconTable} />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className={styles.searchInputTable}
              />
            </div>
            <button
              className={styles.exportCsvButton}
              onClick={handleExportCsv}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <img src={loadingGif} alt="Exportando..." className={styles.exportIconTable} />
                  Exportando...
                </>
              ) : (
                <>
                  <img src={FileExportIcon} alt="Exportar" className={styles.exportIconTable} />
                  Exportar CSV
                </>
              )}
            </button>
          </div>
        
          <div className={styles.tableWrapper}>
          <table className={styles.consolidatedDataTable}>
              <thead>
                <tr>
                  <th>ID do Aluno</th>
                  <th>Data de Nascimento</th>
                  <th style={{ textAlign: 'center' }}>Gênero</th>
                  <th style={{ textAlign: 'center' }}>Estado Civil</th>
                  <th style={{ textAlign: 'center' }}>Status do Aluno</th>
                  <th style={{ textAlign: 'center' }}>Modalidade do Curso</th>
                  <th style={{ textAlign: 'center' }}>Código da Turma</th>
                  <th style={{ textAlign: 'center' }}>Status da Turma</th>
                  <th>Data de Publicação</th>
                  <th style={{ textAlign: 'center' }}>Nome do Curso</th>
                  <th style={{ textAlign: 'center' }}>Área do Curso</th>
                  <th style={{ textAlign: 'center' }}>Cidade do Curso</th>
                  <th style={{ textAlign: 'center' }}>Estado do Curso</th>
                  <th>Data de Aceitação</th>
                  <th>Data de Pré-Matrícula</th>
                  <th>Data de Matrícula</th>
                  <th>Data de Inscrição</th>
                  <th>Data de Criação</th>
                  <th>Data de Atualização</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="19" style={{ textAlign: 'center', padding: '40px' }}>
                      <img src={loadingGif} alt="Carregando..." className={styles.loadingIcon} />
                      <p>Carregando dados consolidados de adesão...</p>
                    </td>
                  </tr>
                ) : processedConsolidatedData.length > 0 ? (
                  processedConsolidatedData.map((item, index) => (
                    <tr key={`${item.studentId}-${index}`}>
                      <td>{item.studentId}</td>
                      <td>{item.dateOfBirth}</td>
                      <td style={{ textAlign: 'center' }}>{item.gender}</td>
                      <td style={{ textAlign: 'center' }}>{item.maritalStatus}</td>
                      <td style={{ textAlign: 'center' }}>{item.studentStatus}</td>
                      <td style={{ textAlign: 'center' }}>{item.courseModality}</td>
                      <td style={{ textAlign: 'center' }}>{item.classCode}</td>
                      <td style={{ textAlign: 'center' }}>{item.classStatus}</td>
                      <td>{item.publicationDate}</td>
                      <td style={{ textAlign: 'center' }}>{item.courseName}</td>
                      <td style={{ textAlign: 'center' }}>{item.courseArea}</td>
                      <td style={{ textAlign: 'center' }}>{item.courseCity}</td>
                      <td style={{ textAlign: 'center' }}>{item.courseState}</td>
                      <td>{item.acceptanceDate}</td>
                      <td>{item.preRegistrationDate}</td>
                      <td>{item.registrationDate}</td>
                      <td>{item.enrollmentDate}</td>
                      <td>{item.createdAt}</td>
                      <td>{item.updatedAt}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="19" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Nenhum dado consolidado de adesão encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </div>
        
        <div className={styles.pagination}>
          {paginationInfo && paginationInfo.totalPages > 1 && (
            <>
              <button
                className={styles.paginationArrow}
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage === 1}
                title="Página anterior"
              >
                ←
              </button>
              
              {getVisiblePages().map((page, index) => (
                <button
                  key={`page-${page}-${index}`}
                  className={
                    page === '...' 
                      ? styles.paginationDots 
                      : page === currentPage 
                        ? styles.activePage 
                        : styles.paginationButton
                  }
                  onClick={() => page !== '...' && handlePageClick(page)}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}
              
              <button
                className={styles.paginationArrow}
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage === paginationInfo.totalPages}
                title="Próxima página"
              >
                →
              </button>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default AdhesionConsolidatedData;

